import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Card, CardContent, IconButton,
  FormControl, InputLabel, Select, MenuItem,
  Snackbar, Alert, Backdrop, CircularProgress,
  Divider, Tooltip, useTheme, useMediaQuery,
  ToggleButtonGroup, ToggleButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import {
  Add, Cancel, Save, Edit, Delete,
  ArrowBack, FilterList, Menu as MenuIcon,
  InsertChartOutlined, Api as ApiIcon,
} from '@mui/icons-material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import CloseIcon from '@mui/icons-material/Close';
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import apiClient from 'utils/ApiClient';
import { setDefaultReportSuiteForConfig } from '../reportsDefaultServer';
import { AnalyticsIngestApiDialog } from './AnalyticsIngestApiDialog';

interface ReportSuite {
  _id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

interface ModuleConfig {
  _id: string;
  moduleType: string;
  name: string;
  tenantId?: string;
  siteId?: string;
  defaultReportSuiteId?: string;
}

type DialogMode = 'create' | 'edit';
type SortByField = 'createdAt' | 'name' | 'updatedAt';
type SortOrder = 'asc' | 'desc';
type ViewMode = 'tile' | 'table';

const VIEWMODE_STORAGE_KEY = 'report-suites-view-mode';

export const ReportSuiteManager: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { configId } = useParams<{ configId: string }>();

  const [suites, setSuites] = useState<ReportSuite[]>([]);
  const [parentConfig, setParentConfig] = useState<ModuleConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState<SortByField>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
    return stored === 'table' ? 'table' : 'tile';
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>('create');
  const [editingSuiteId, setEditingSuiteId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [apiDialogOpen, setApiDialogOpen] = useState(false);

  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'info',
  });

  const fetchParentConfig = useCallback(async () => {
    try {
      const res: any = await apiClient.get('/il/settings/module-configs');
      const configs: ModuleConfig[] = Array.isArray(res?.data) ? res.data : (res?.data?.moduleConfigurations ?? []);
      const found = configs.find((c) => c._id === configId);
      if (found) setParentConfig(found);
    } catch { /* silent */ }
  }, [configId]);

  const fetchSuites = useCallback(async () => {
    if (!configId) return;
    setLoading(true);
    try {
      const res: any = await apiClient.get(`/il/settings/module-config/${configId}/report-suites`);
      setSuites(res?.data?.reportSuites ?? []);
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Failed to load report suites', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [configId]);

  useEffect(() => { fetchParentConfig(); }, [fetchParentConfig]);
  useEffect(() => { fetchSuites(); }, [fetchSuites]);

  const displayedSuites = useMemo(() => {
    let list = [...suites];
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      list = list.filter((s) => s.name.toLowerCase().includes(q) || s.description?.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      const va = (a as any)[sortBy] || '';
      const vb = (b as any)[sortBy] || '';
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : 0;
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [suites, searchText, sortBy, sortOrder]);

  const handleOpenCreate = () => {
    setDialogMode('create');
    setEditingSuiteId(null);
    setFormName('');
    setFormDescription('');
    setDialogOpen(true);
  };

  const handleOpenEdit = (suite: ReportSuite) => {
    setDialogMode('edit');
    setEditingSuiteId(suite._id);
    setFormName(suite.name);
    setFormDescription(suite.description || '');
    setDialogOpen(true);
  };

  const handleDialogClose = () => { setDialogOpen(false); setEditingSuiteId(null); };

  const handleSubmit = async () => {
    if (!formName.trim() || !configId) return;
    setLoading(true);
    try {
      if (dialogMode === 'edit' && editingSuiteId) {
        await apiClient.patch(`/il/settings/module-config/${configId}/report-suites/${editingSuiteId}`, {
          name: formName.trim(),
          description: formDescription.trim(),
        });
        setSnackbar({ open: true, message: 'Report suite updated', severity: 'success' });
      } else {
        await apiClient.post(`/il/settings/module-config/${configId}/report-suites`, {
          name: formName.trim(),
          description: formDescription.trim(),
        });
        setSnackbar({ open: true, message: 'Report suite created', severity: 'success' });
      }
      handleDialogClose();
      fetchSuites();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Failed to save', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (suiteId: string) => {
    if (!window.confirm('Are you sure you want to delete this report suite?')) return;
    setLoading(true);
    try {
      await apiClient.delete(`/il/settings/module-config/${configId}/report-suites/${suiteId}`);
      if (parentConfig?.defaultReportSuiteId === suiteId) {
        setParentConfig((prev) => (prev ? { ...prev, defaultReportSuiteId: '' } : prev));
      }
      setSnackbar({ open: true, message: 'Report suite deleted', severity: 'success' });
      fetchSuites();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Failed to delete', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefaultReportSuite = async (suite: ReportSuite) => {
    if (!configId) return;
    try {
      setLoading(true);
      await setDefaultReportSuiteForConfig(configId, suite._id);
      setParentConfig((prev) => (prev ? { ...prev, defaultReportSuiteId: suite._id } : prev));
      setSnackbar({
        open: true,
        message: `"${suite.name}" is now the default report suite for Reports`,
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Failed to set default report suite', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const defaultReportSuiteId = parentConfig?.defaultReportSuiteId || '';

  const goBack = () => navigate('/infralytiqs/settings');

  return (
    <Box sx={{ pl: { xs: 1.25, md: 2.5 }, pt: 1, width: '100%', mb: 3 }}>
      {loading && (
        <Backdrop
          sx={(theme: any) => ({ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 })}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {/* Breadcrumb: Home > Infralytiqs Settings > Config Name */}
      <DynamicBreadcrumbs items={[
        { itemPath: '/dashboard', itemLabel: 'Home' },
        { itemPath: '/infralytiqs/settings', itemLabel: 'Infralytiqs Settings' },
        { itemPath: '', itemLabel: parentConfig?.name || 'Configuration' },
      ]} />

      {/* Page Title */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Back to Settings">
            <IconButton size="small" onClick={goBack}>
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Report Suites
          </Typography>
          {parentConfig?.name && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {parentConfig.name}
            </Typography>
          )}
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Mark a report suite with the green tick to preselect it on the Reports page when this ClickHouse server is chosen.
      </Typography>

      {/* Mobile: prominent CREATE + API buttons above toolbar */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.25,
          mb: 2,
          px: { xs: 0.5, sm: 1 },
        }}
      >
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<Add />}
          onClick={handleOpenCreate}
          fullWidth
          sx={{
            maxWidth: 480,
            py: 1.25,
            fontWeight: 700,
            letterSpacing: '0.04em',
            borderWidth: 2,
            borderColor: 'secondary.main',
            color: 'secondary.main',
            textTransform: 'uppercase',
            '&:hover': { borderWidth: 2, borderColor: 'secondary.dark' },
          }}
        >
          New Report Suite
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ApiIcon />}
          onClick={() => setApiDialogOpen(true)}
          fullWidth
          sx={{
            maxWidth: 480,
            py: 1.1,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          API
        </Button>
      </Box>

      {/* Toolbar (matching DataSets design) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: { xs: 1, md: 2 },
          alignItems: 'center',
          mb: 3,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
        }}
      >
        {/* Mobile: Filter Icon Button */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
          <IconButton
            color="secondary"
            onClick={() => setMobileFilterOpen(true)}
            size="large"
            sx={{
              border: '1px solid',
              borderColor: 'secondary.main',
              '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' },
            }}
          >
            <FilterList />
          </IconButton>
        </Box>

        {/* Mobile: Menu Icon Button */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', ml: 'auto' }}>
          <IconButton
            color="secondary"
            onClick={() => setMobileActionsOpen(true)}
            size="large"
            sx={{
              border: '1px solid',
              borderColor: 'secondary.main',
              '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Desktop: Search Box */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
          <TextField
            label="Search Report Suites"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ flexGrow: 1, minWidth: '200px' }}
          />
        </Box>

        {/* Desktop: Sort Controls */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'row',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Sort By</InputLabel>
            <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value as SortByField)}>
              <MenuItem value="createdAt">Created Date</MenuItem>
              <MenuItem value="updatedAt">Updated Date</MenuItem>
              <MenuItem value="name">Name</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 100 }} size="small">
            <InputLabel>Order</InputLabel>
            <Select value={sortOrder} label="Order" onChange={(e) => setSortOrder(e.target.value as SortOrder)}>
              <MenuItem value="asc">ASC</MenuItem>
              <MenuItem value="desc">DSC</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Desktop: Action Buttons */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'row',
            gap: 1,
            alignItems: 'center',
            marginLeft: 0,
          }}
        >
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_e, newMode) => {
              if (newMode !== null) {
                setViewMode(newMode);
                localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                border: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                  backgroundColor: (t) => t.palette.toggle?.selectedBg ?? t.palette.secondary.main,
                  color: (t) => t.palette.toggle?.selectedColor ?? t.palette.secondary.contrastText,
                  '&:hover': { backgroundColor: (t) => t.palette.toggle?.selectedHoverBg ?? t.palette.secondary.dark },
                },
              },
            }}
          >
            <ToggleButton value="tile" aria-label="Tile view">
              <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table view">
              <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ApiIcon />}
              onClick={() => setApiDialogOpen(true)}
              size="small"
              sx={{ whiteSpace: 'nowrap' }}
            >
              API
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Add />}
              onClick={handleOpenCreate}
              size="small"
              sx={{ whiteSpace: 'nowrap' }}
            >
              New Report Suite
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Mobile Filter Dialog */}
      <Dialog open={mobileFilterOpen} onClose={() => setMobileFilterOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Search &amp; Filters</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Search Report Suites"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              fullWidth
            />
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value as SortByField)}>
                <MenuItem value="createdAt">Created Date</MenuItem>
                <MenuItem value="updatedAt">Updated Date</MenuItem>
                <MenuItem value="name">Name</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Order</InputLabel>
              <Select value={sortOrder} label="Order" onChange={(e) => setSortOrder(e.target.value as SortOrder)}>
                <MenuItem value="asc">ASC</MenuItem>
                <MenuItem value="desc">DSC</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMobileFilterOpen(false)} variant="outlined" color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile Actions Dialog */}
      <Dialog open={mobileActionsOpen} onClose={() => setMobileActionsOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Actions</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ mr: 1 }}>View:</Typography>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_e, newMode) => {
                  if (newMode !== null) {
                    setViewMode(newMode);
                    localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
                  }
                }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    border: '1px solid',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      backgroundColor: (t) => t.palette.toggle?.selectedBg ?? t.palette.secondary.main,
                      color: (t) => t.palette.toggle?.selectedColor ?? t.palette.secondary.contrastText,
                      '&:hover': { backgroundColor: (t) => t.palette.toggle?.selectedHoverBg ?? t.palette.secondary.dark },
                    },
                  },
                }}
              >
                <ToggleButton value="tile" aria-label="Tile view">
                  <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
                </ToggleButton>
                <ToggleButton value="table" aria-label="Table view">
                  <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ApiIcon />}
              onClick={() => {
                setMobileActionsOpen(false);
                setApiDialogOpen(true);
              }}
              fullWidth
            >
              API
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMobileActionsOpen(false)} variant="outlined" color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <AnalyticsIngestApiDialog
        open={apiDialogOpen}
        onClose={() => setApiDialogOpen(false)}
        tenantId={parentConfig?.tenantId || ''}
        siteId={parentConfig?.siteId || ''}
      />

      {/* Content */}
      {!loading && displayedSuites.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <AssessmentOutlined sx={{ fontSize: 64, color: theme.palette.action.disabled, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {searchText ? 'No report suites match your search' : 'No report suites yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchText ? 'Try a different search term' : 'Create your first report suite for this configuration'}
          </Typography>
          {!searchText && (
            <Button variant="contained" color="secondary" startIcon={<Add />} onClick={handleOpenCreate}>
              Create Report Suite
            </Button>
          )}
        </Box>
      ) : viewMode === 'tile' ? (
        /* ─── Tile View ─── */
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, justifyContent: { xs: 'center', lg: 'flex-start' } }}>
          {displayedSuites.map((suite) => {
            const isDefaultReportSuite = defaultReportSuiteId === suite._id;
            return (
            <Card
              key={suite._id}
              variant="outlined"
              sx={{
                width: { xs: '100%', sm: 280 },
                borderRadius: 2,
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: theme.shadows[4] },
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}
            >
              {isDefaultReportSuite && (
                <Tooltip title="Default report suite for Reports">
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 18, color: 'success.contrastText' }} />
                  </Box>
                </Tooltip>
              )}
              <CardContent sx={{ flex: 1, pb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom noWrap>
                  {suite.name}
                </Typography>
                {suite.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {suite.description}
                  </Typography>
                )}
                <Typography variant="caption" color="text.disabled">
                  Created: {new Date(suite.createdAt).toLocaleDateString()}
                </Typography>
              </CardContent>
              <Divider />
              {/*
                Action buttons mirror the design used on the
                /#/infralytiqs/settings configuration tiles (see Settings.tsx —
                `overlayPanel`): contained, small, square-ish buttons with a
                compact 16px icon. Using <Button> instead of <IconButton> gives
                us the filled secondary/info/error color treatment for free.
              */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 0.75, gap: 0.5 }}>
                {!isDefaultReportSuite && (
                  <Tooltip title="Set as default report suite for Reports">
                    <Button
                      color="success"
                      variant="outlined"
                      size="small"
                      onClick={() => { void handleSetDefaultReportSuite(suite); }}
                      sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                      aria-label="Set as default report suite"
                    >
                      <CheckCircle sx={{ fontSize: 16 }} />
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title="Manage Reports">
                  <Button
                    color="info"
                    variant="contained"
                    size="small"
                    onClick={() => navigate(`/infralytiqs/settings/${configId}/suites/${suite._id}`)}
                    sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                    aria-label="Manage Reports"
                  >
                    <InsertChartOutlined sx={{ fontSize: 16 }} />
                  </Button>
                </Tooltip>
                <Tooltip title="Edit">
                  <Button
                    color="secondary"
                    variant="contained"
                    size="small"
                    onClick={() => handleOpenEdit(suite)}
                    sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                    aria-label="Edit Report Suite"
                  >
                    <Edit sx={{ fontSize: 16 }} />
                  </Button>
                </Tooltip>
                <Tooltip title="Delete">
                  <Button
                    color="error"
                    variant="contained"
                    size="small"
                    onClick={() => handleDelete(suite._id)}
                    sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                    aria-label="Delete Report Suite"
                  >
                    <Delete sx={{ fontSize: 16 }} />
                  </Button>
                </Tooltip>
              </Box>
            </Card>
            );
          })}
        </Box>
      ) : (
        /* ─── Table View ─── */
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Updated</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedSuites.map((suite) => {
                const isDefaultReportSuite = defaultReportSuiteId === suite._id;
                return (
                <TableRow key={suite._id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isDefaultReportSuite && (
                        <Tooltip title="Default report suite for Reports">
                          <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                        </Tooltip>
                      )}
                      <span>{suite.name}</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {suite.description || '—'}
                  </TableCell>
                  <TableCell>{new Date(suite.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(suite.updatedAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                      {!isDefaultReportSuite && (
                        <Tooltip title="Set as default report suite for Reports">
                          <Button
                            color="success"
                            variant="outlined"
                            size="small"
                            onClick={() => { void handleSetDefaultReportSuite(suite); }}
                            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                            aria-label="Set as default report suite"
                          >
                            <CheckCircle sx={{ fontSize: 16 }} />
                          </Button>
                        </Tooltip>
                      )}
                      <Tooltip title="Manage Reports">
                        <Button
                          color="info"
                          variant="contained"
                          size="small"
                          onClick={() => navigate(`/infralytiqs/settings/${configId}/suites/${suite._id}`)}
                          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                          aria-label="Manage Reports"
                        >
                          <InsertChartOutlined sx={{ fontSize: 16 }} />
                        </Button>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <Button
                          color="secondary"
                          variant="contained"
                          size="small"
                          onClick={() => handleOpenEdit(suite)}
                          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                          aria-label="Edit Report Suite"
                        >
                          <Edit sx={{ fontSize: 16 }} />
                        </Button>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Button
                          color="error"
                          variant="contained"
                          size="small"
                          onClick={() => handleDelete(suite._id)}
                          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                          aria-label="Delete Report Suite"
                        >
                          <Delete sx={{ fontSize: 16 }} />
                        </Button>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle
          component="div"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Typography variant="h6" component="span" fontWeight={600}>
            {dialogMode === 'edit' ? 'Edit Report Suite' : 'Create Report Suite'}
          </Typography>
          <IconButton onClick={handleDialogClose} size="small" edge="end"><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="Suite Name"
            fullWidth
            required
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="e.g. Production Analytics Suite"
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={3}
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            placeholder="Describe what this report suite is for…"
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={handleDialogClose} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            startIcon={<Save />}
            disabled={!formName.trim()}
          >
            {dialogMode === 'edit' ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((p) => ({ ...p, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
